/**
 * Styling Tab Component
 * 
 * @package TopBuddy
 */

import { __ } from '@wordpress/i18n';
import {
	ColorPicker,
	Dropdown, 
	ColorIndicator, 
	Button, 
	SelectControl, 
	__experimentalToggleGroupControl as ToggleGroupControl,
	__experimentalToggleGroupControlOption as ToggleGroupControlOption,
	GradientPicker,
	__experimentalDivider as Divider,
} from '@wordpress/components';
import ProBadge from './ProBadge';
import { useState } from '@wordpress/element';

const StylingTab = ({ settings, updateSetting }) => {
	const paddingOptions = [
		{ label: __('Small', 'topbuddy'), value: 'small' },
		{ label: __('Medium', 'topbuddy'), value: 'medium' },
		{ label: __('Large', 'topbuddy'), value: 'large' },
		{ label: __('Extra Large', 'topbuddy'), value: 'xlarge' },
	];

	const backgroundType = settings.backgroundType || (settings.backgroundGradient ? 'gradient' : 'classic');

	const postSettingsData = window.topbuddyPostSettings || {};
	const isProActive = postSettingsData.isProActive || false;
	const upgradeUrl = postSettingsData.upgradeUrl || 'https://wptopbuddy.com/?utm_source=plugin&utm_medium=topbuddy-free-plugin&utm_campaign=topbuddy-free-plugin-admin';

	const [imageFrame, setImageFrame] = useState(null);

	const handleImageUpload = (e) => {
		e.preventDefault();

		if (imageFrame) {
			imageFrame.open();
			return;
		}

		const frame = wp.media({
			title: __('Select or Upload Background Image', 'topbuddy'),
			button: {
				text: __('Use this image', 'topbuddy'),
			},
			multiple: false,
		});

		frame.on('select', function () {
			const attachment = frame.state().get('selection').first().toJSON();
			updateSetting('backgroundImage', attachment.url);
		});

		setImageFrame(frame);
		frame.open();
	};

	const handleImageRemove = (e) => {
		e.preventDefault();
		updateSetting('backgroundImage', '');
	};

	return (
		<div className="topbuddy-settings__tab-content">
			<div className="topbuddy-settings__field">
				<ToggleGroupControl
					label={__('Background', 'topbuddy')}
					value={backgroundType}
					onChange={(value) => updateSetting('backgroundType', value)}
					isBlock
					style={{ maxWidth: '300px' }}
				>
					<ToggleGroupControlOption value="classic" label={__('Classic', 'topbuddy')} />
					<ToggleGroupControlOption value="gradient" label={__('Gradient', 'topbuddy')} />
				</ToggleGroupControl>
			</div>

			{backgroundType === 'classic' && (
				<>
			<div className="topbuddy-settings__field">
				<label className="topbuddy-settings-field__label topbuddy-settings-field__label--color">{__('Background Color', 'topbuddy')}</label>
				<div className="topbuddy-settings__color-picker">
					<Dropdown
						className="topbuddy-color-dropdown"
						contentClassName="topbuddy-color-dropdown__content"
						popoverProps={{ placement: 'bottom-start' }}
						renderToggle={({ isOpen, onToggle }) => (
							<Button
								onClick={onToggle}
								aria-expanded={isOpen}
								className="topbuddy-color-dropdown__toggle"
							>
								<ColorIndicator colorValue={settings.backgroundColor} />
								<span className="topbuddy-color-dropdown__label">
									{settings.backgroundColor}
								</span>
							</Button>
						)}
						renderContent={() => (
							<ColorPicker
								color={settings.backgroundColor}
								onChange={(value) => updateSetting('backgroundColor', value)}
								enableAlpha={false}
							/>
						)}
					/>
				</div>
			</div>

			{/* Background Image Field - Pro */}
			<div className="topbuddy-settings__field">
				<label className="topbuddy-settings-field__label">
					{__('Background Image', 'topbuddy')}
					{!isProActive && (
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
							<ProBadge />
							<Button
								variant="link"
								href={upgradeUrl}
								target="_blank"
								rel="noopener noreferrer"
								style={{ textDecoration: 'none', textTransform: 'none' }}
							>
								{__('Upgrade', 'topbuddy')}
								<span className="dashicons dashicons-external" style={{ marginLeft: '5px' }}></span>
							</Button>
						</span>
					)}
				</label>
				<div className="topbuddy-settings__image-upload">
					<div style={{ opacity: isProActive ? 1 : 0.5, pointerEvents: isProActive ? 'auto' : 'none' }}>
						{settings.backgroundImage ? (
							<div className="topbuddy-settings__image-preview" style={{ marginBottom: '10px' }}>
								<img 
									src={settings.backgroundImage} 
									alt={__('Background Image', 'topbuddy')} 
									style={{ maxWidth: '200px', height: 'auto', display: 'block', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
								/>
								<Button
									isDestructive
									size="small"
									onClick={handleImageRemove}
								>
									{__('Remove Image', 'topbuddy')}
								</Button>
							</div>
						) : (
							<Button
								variant="secondary"
								size="small"
								onClick={handleImageUpload}
							>
								{__('Upload Image', 'topbuddy')}
							</Button>
						)}
					</div>
				</div>
			</div>

			{/* Background Overlay Opacity - Conditional Pro */}
			{settings.backgroundImage && (
				<div className="topbuddy-settings__field">
					<label className="topbuddy-settings-field__label">
						{__('Background Overlay Opacity', 'topbuddy')}
						{!isProActive && (
							<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
								<ProBadge />
								<Button
									variant="link"
									href={upgradeUrl}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'none', textTransform: 'none' }}
								>
									{__('Upgrade', 'topbuddy')}
									<span className="dashicons dashicons-external" style={{ marginLeft: '5px' }}></span>
								</Button>
							</span>
						)}
					</label>
					<div>
						<input
							type="number"
							min="0"
							max="1"
							step="0.1"
							value={settings.backgroundOverlayOpacity !== undefined ? settings.backgroundOverlayOpacity : 0.6}
							onChange={(e) => updateSetting('backgroundOverlayOpacity', parseFloat(e.target.value))}
							disabled={!isProActive}
							style={{
								width: '100%',
								padding: '8px',
								border: '1px solid #ddd',
								borderRadius: '4px',
								fontSize: '14px',
								opacity: isProActive ? 1 : 0.5
							}}
						/>
						<p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
							{__('Controls the opacity of the background color overlay (0 = transparent, 1 = solid)', 'topbuddy')}
						</p>
					</div>
				</div>
			)}
				</>
			)}

			{backgroundType === 'gradient' && (
				<div className="topbuddy-settings__field">
					<label className="topbuddy-settings-field__label">
						{__('Background Gradient', 'topbuddy')}
						{!isProActive && (
							<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
								<ProBadge />
								<Button
									variant="link"
									href={upgradeUrl}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'none', textTransform: 'none' }}
								>
									{__('Upgrade', 'topbuddy')}
									<span className="dashicons dashicons-external" style={{ marginLeft: '5px' }}></span>
								</Button>
							</span>
						)}
					</label>
					<div style={{ maxWidth: '400px', opacity: isProActive ? 1 : 0.5, pointerEvents: isProActive ? 'auto' : 'none' }}>
						<GradientPicker
							aria-label={__('Background Gradient', 'topbuddy')}
							value={settings.backgroundGradient || null}
							onChange={(value) => updateSetting('backgroundGradient', value || '')}
							gradients={[]}
							clearable={true}
						/>
					</div>
				</div>
			)}

			<Divider style={{ marginTop: '5px', marginBottom: '20px' }} />

			<div className="topbuddy-settings__field">
				<SelectControl
					label={__('Padding', 'topbuddy')}
					value={settings.padding || 'medium'}
					options={paddingOptions}
					onChange={(value) => updateSetting('padding', value)}
					__nextHasNoMarginBottom
					__next40pxDefaultSize
				/>
			</div>

			<Divider style={{ marginTop: '5px', marginBottom: '20px' }} />

			<div className="topbuddy-settings__field topbuddy-settings-field--color">
				<label className="topbuddy-settings-field__label">{__('Close Button Color', 'topbuddy')}</label>
				<div className="topbuddy-settings__color-picker">
					<Dropdown
						className="topbuddy-color-dropdown"
						contentClassName="topbuddy-color-dropdown__content"
						popoverProps={{ placement: 'bottom-start' }}
						renderToggle={({ isOpen, onToggle }) => (
							<Button
								onClick={onToggle}
								aria-expanded={isOpen}
								className="topbuddy-color-dropdown__toggle"
							>
								<ColorIndicator colorValue={settings.closeColor} />
								<span className="topbuddy-color-dropdown__label">
									{settings.closeColor}
								</span>
							</Button>
						)}
						renderContent={() => (
							<ColorPicker
								color={settings.closeColor}
								onChange={(value) => updateSetting('closeColor', value)}
								enableAlpha={false}
							/>
						)}
					/>
				</div>
			</div>

			<div className="topbuddy-settings__field topbuddy-settings-field--color">
				<label className="topbuddy-settings-field__label">{__('Close Button Hover Color', 'topbuddy')}</label>
				<div className="topbuddy-settings__color-picker">
					<Dropdown
						className="topbuddy-color-dropdown"
						contentClassName="topbuddy-color-dropdown__content"
						popoverProps={{ placement: 'bottom-start' }}
						renderToggle={({ isOpen, onToggle }) => (
							<Button
								onClick={onToggle}
								aria-expanded={isOpen}
								className="topbuddy-color-dropdown__toggle"
							>
								<ColorIndicator colorValue={settings.closeHoverColor} />
								<span className="topbuddy-color-dropdown__label">
									{settings.closeHoverColor}
								</span>
							</Button>
						)}
						renderContent={() => (
							<ColorPicker
								color={settings.closeHoverColor}
								onChange={(value) => updateSetting('closeHoverColor', value)}
								enableAlpha={false}
							/>
						)}
					/>
				</div>
			</div>

			<div className="topbuddy-settings__field topbuddy-settings-field--color">
				<label className="topbuddy-settings-field__label">{__('Close Button Background Color', 'topbuddy')}</label>
				<div className="topbuddy-settings__color-picker">
					<Dropdown
						className="topbuddy-color-dropdown"
						contentClassName="topbuddy-color-dropdown__content"
						popoverProps={{ placement: 'bottom-start' }}
						renderToggle={({ isOpen, onToggle }) => (
							<Button
								onClick={onToggle}
								aria-expanded={isOpen}
								className="topbuddy-color-dropdown__toggle"
							>
								<ColorIndicator colorValue={settings.closeBgColor} />
								<span className="topbuddy-color-dropdown__label">
									{settings.closeBgColor}
								</span>
							</Button>
						)}
						renderContent={() => (
							<ColorPicker
								color={settings.closeBgColor}
								onChange={(value) => updateSetting('closeBgColor', value)}
								enableAlpha={false}
							/>
						)}
					/>
				</div>
			</div>

			<div className="topbuddy-settings__field topbuddy-settings-field--color">
				<label className="topbuddy-settings-field__label">{__('Close Button Hover Background Color', 'topbuddy')}</label>
				<div className="topbuddy-settings__color-picker">
					<Dropdown
						className="topbuddy-color-dropdown"
						contentClassName="topbuddy-color-dropdown__content"
						popoverProps={{ placement: 'bottom-start' }}
						renderToggle={({ isOpen, onToggle }) => (
							<Button
								onClick={onToggle}
								aria-expanded={isOpen}
								className="topbuddy-color-dropdown__toggle"
							>
								<ColorIndicator colorValue={settings.closeHoverBgColor} />
								<span className="topbuddy-color-dropdown__label">
									{settings.closeHoverBgColor}
								</span>
							</Button>
						)}
						renderContent={() => (
							<ColorPicker
								color={settings.closeHoverBgColor}
								onChange={(value) => updateSetting('closeHoverBgColor', value)}
								enableAlpha={false}
							/>
						)}
					/>
				</div>
			</div>
			
		</div>
	);
};

export default StylingTab;
